/*
 * v1.15.0 — Platform-admin navigation tree, restructured into the 9
 * top-level modules from the navigation-restructuring spec §3.1:
 *
 *   1. Dashboard
 *   2. Tenancy & Billing
 *   3. Data Sources & Sync
 *   4. Variables & Aggregation
 *   5. Indicators & Publications
 *   6. Analysis & Exploration
 *   7. Access & Permissions
 *   8. Monitoring & Operations
 *   9. Settings
 *
 * Non-breaking refactor:
 *   - Every existing `to:` path is preserved verbatim. Routes already
 *     mounted in App.tsx continue to resolve. /admin/* remains the
 *     canonical URL prefix for the platform console.
 *   - Items move between groups only; no item is removed, no permission
 *     gate is changed.
 *   - Visibility predicates are unchanged from the prior 10-group
 *     layout — usePlatformPermissions stays authoritative.
 */

import type { NavTree } from "./navigationTypes.js";

export const platformAdminNavigation: NavTree = {
  console: "platform-admin",
  basePath: "/admin",
  groups: [
    // --- 1. Dashboard ---------------------------------------------------
    {
      id: "dashboard",
      label: "Dashboard",
      items: [
        {
          to: "/admin",
          label: "Platform Overview",
          description: "Cross-tenant dashboard: workspaces, sync health, ingestion, payments, adapter health.",
          status: "active"
        }
      ]
    },

    // --- 2. Tenancy & Billing -------------------------------------------
    // Merges legacy "Tenancy" + "Monetisation" since both surface
    // tenant-lifecycle concerns (one creates the tenant, the other bills
    // it). Items keep their existing routes.
    {
      id: "tenancy-billing",
      label: "Tenancy & Billing",
      requireAnyPermission: [
        "platform.tenants.view",
        "platform.tenants.manage",
        "platform.plans.view",
        "platform.payments.view",
        "platform.billing.view"
      ],
      items: [
        {
          to: "/admin/workspaces",
          label: "Workspaces",
          description: "List, search, filter, create, suspend, restore tenants.",
          status: "active",
          requireAnyPermission: ["platform.tenants.view", "platform.tenants.manage"]
        },
        {
          to: "/admin/administration/monetisation/plans",
          label: "Subscription plans",
          description: "Plan catalog: features, quotas, pricing.",
          status: "active",
          requireAnyPermission: ["platform.plans.view", "platform.plans.manage"]
        },
        {
          to: "/admin/administration/monetisation/payment-gateways",
          label: "Payment gateways",
          description: "Configure providers, test connections, rotate payment secrets.",
          status: "active",
          requireAnyPermission: ["platform.payments.view", "platform.payments.manage"]
        },
        {
          to: "/admin/administration/monetisation/billing",
          label: "Billing & refunds",
          description: "Invoicing, refunds, dunning state.",
          status: "active",
          requireAnyPermission: ["platform.billing.view", "platform.billing.manage", "platform.refunds.manage"]
        },
        {
          to: "/admin/monetisation/transactions",
          label: "Transactions",
          description: "Per-tenant payment transaction log with provider correlation IDs.",
          status: "active"
        },
        {
          to: "/admin/monetisation/invoices",
          label: "Invoices",
          description: "Invoice history across tenants; download / regenerate.",
          status: "active"
        },
        {
          to: "/admin/monetisation/usage-quotas",
          label: "Usage & quotas",
          description: "Cross-tenant usage vs plan quotas; over-quota alerts.",
          status: "active"
        }
      ]
    },

    // --- 3. Data Sources & Sync -----------------------------------------
    // Merges legacy "DHIS2 Integration" + "Sync & Streaming". Both are
    // about getting data INTO the platform from a source system.
    {
      id: "data-sources-sync",
      label: "Data Sources & Sync",
      requireAnyPermission: [
        "platform.tenants.view",
        "platform.connections.diagnose",
        "platform.operations.manage"
      ],
      items: [
        {
          to: "/admin/administration/dhis2/instances",
          label: "DHIS2 instances",
          description: "Service profiles across all tenants; credentials; rotation status.",
          status: "active"
        },
        {
          to: "/admin/administration/dhis2/app-clients",
          label: "DHIS2 app clients",
          description: "Registered embedded-app clients, authorities, secret rotation.",
          status: "active"
        },
        {
          to: "/admin/dhis2/app-installations",
          label: "App installation status",
          description: "Per-instance install state, app version, last heartbeat.",
          status: "active"
        },
        {
          to: "/admin/dhis2/secrets",
          label: "DHIS2 credentials & secrets",
          description: "Encrypted secret inventory and rotation for DHIS2 connections.",
          status: "active",
          requireAnyPermission: ["platform.secrets.status.view", "platform.security.view"]
        },
        {
          to: "/admin/dhis-metadata",
          label: "Metadata browser",
          description: "Cross-tenant DHIS2 metadata snapshots, lazy-loaded per workspace.",
          status: "active"
        },
        {
          to: "/admin/dhis2/metadata-snapshots",
          label: "Metadata snapshots",
          description: "Current synced metadata snapshot per workspace and source type.",
          status: "active"
        },
        {
          to: "/admin/live",
          label: "Live monitor",
          description: "Real-time metadata/data/tally/aggregate/indicator/job/DLQ feed.",
          status: "active"
        },
        {
          to: "/admin/sync/stream-console",
          label: "Stream console",
          description: "Per-instance ingestion, accept/duplicate/reject counts, manual sync.",
          status: "active"
        },
        {
          to: "/admin/sync/transfer-logs",
          label: "Transfer logs",
          description: "Comprehensive ingestion + forwarding run table with retry/DLQ actions.",
          status: "active"
        },
        {
          to: "/admin/sync/watermarks",
          label: "Watermarks",
          description: "Metadata/tracker/aggregate/app-stream watermarks; lag; reset; replay-from.",
          status: "active"
        },
        {
          to: "/admin/sync/dead-letter",
          label: "Jobs & dead-letter",
          description: "DLQ inspection, retry, replay, export failure details.",
          status: "active"
        },
        {
          to: "/admin/sync/replay-repair",
          label: "Replay & repair",
          description: "Replay record/window/program/dataset/org-unit; restate rollups; repair contributions.",
          status: "active"
        },
        {
          to: "/admin/administration/dhis2/forwarding",
          label: "Forwarding oversight",
          description: "Cross-tenant visibility into outbound forwarding destinations, schedules, and recent run health.",
          status: "active"
        }
      ]
    },

    // --- 4. Variables & Aggregation -------------------------------------
    {
      id: "variables-aggregation",
      label: "Variables & Aggregation",
      requireAnyPermission: ["platform.tenants.view", "platform.operations.manage"],
      items: [
        {
          to: "/admin/metadata/tally-variables",
          label: "TallyMetric variables",
          description: "Auto-generated catalogue of every Metric Variable derived from synced metadata — the structural map of what can be tallied.",
          status: "active"
        },
        {
          to: "/admin/data/tallymetrics",
          label: "TallyMetrics (data)",
          description: "Tallied counts and totals — bucket data computed from tracker events against the auto-generated variable catalogue.",
          status: "active"
        },
        {
          to: "/admin/aggregate-domains",
          label: "Aggregate domains",
          description: "Domain schemas, rollup rules, validation rules, impacted indicators.",
          status: "active"
        },
        {
          to: "/admin/data/mappings",
          label: "Mapping console",
          description: "Tally→indicator, tracker→aggregate, aggregate→publication mapping with preview.",
          status: "active"
        },
        {
          to: "/admin/reconciliation",
          label: "Reconciliation & integrity",
          description: "Source vs platform, source vs rollup, tracker vs aggregate variance.",
          status: "active"
        }
      ]
    },

    // --- 5. Indicators & Publications -----------------------------------
    {
      id: "indicators-publications",
      label: "Indicators & Publications",
      requireAnyPermission: ["platform.public_portals.view", "platform.tenants.view"],
      items: [
        {
          to: "/admin/data/rollups-indicators",
          label: "Rollups & indicators",
          description: "Rollup jobs, indicator definitions, indicator values, manual rollup actions.",
          status: "active"
        },
        {
          to: "/admin/publications",
          label: "Publication profiles",
          description: "Configure DHIS2 / CSV / API / portal targets for outward-facing data.",
          status: "active"
        },
        {
          to: "/admin/publications/runs",
          label: "Publication runs",
          description: "History of publish attempts with retry, dry-run, and audit.",
          status: "active"
        },
        {
          to: "/admin/publications/targets",
          label: "Publication targets",
          description: "Registered target endpoints (DHIS2 servers, webhooks, file drops).",
          status: "active"
        },
        {
          to: "/admin/portals",
          label: "Public portals",
          description: "Manage workspace-scoped public portals.",
          status: "active"
        },
        {
          to: "/admin/portals/datasets",
          label: "Portal datasets",
          description: "Datasets currently exposed via public portals; per-portal inventory.",
          status: "active"
        }
      ]
    },

    // --- 6. Analysis & Exploration --------------------------------------
    {
      id: "analysis-exploration",
      label: "Analysis & Exploration",
      requireAnyPermission: ["platform.tenants.view"],
      items: [
        {
          to: "/admin/analytics",
          label: "Analytics explorer",
          description: "Pivot/chart/table analytics across rollup facts with drill-into-lineage.",
          status: "active"
        },
        {
          to: "/admin/data/warehouse",
          label: "Warehouse explorer",
          description: "Read-only warehouse queries; ClickHouse-backed where configured.",
          status: "active"
        },
        {
          to: "/admin/data/lineage",
          label: "Lineage explorer",
          description: "Source fact → contribution → bucket → indicator → publication trail.",
          status: "active"
        },
        {
          to: "/admin/datasets",
          label: "Datasets",
          description: "Cross-tenant dataset inventory with source domain, refresh status, sensitivity.",
          status: "active"
        }
      ]
    },

    // --- 7. Access & Permissions ----------------------------------------
    // Renamed from "Identity & Access" — same items, spec wording.
    {
      id: "access-permissions",
      label: "Access & Permissions",
      requireAnyPermission: ["platform.users.view", "platform.roles.view", "platform.audit.view"],
      items: [
        {
          to: "/admin/administration/identity/members",
          label: "Members",
          description: "Platform member directory; invite/suspend/reactivate.",
          status: "active",
          requireAnyPermission: ["platform.users.view", "platform.users.manage"]
        },
        {
          to: "/admin/administration/identity/roles",
          label: "Roles & permissions",
          description: "Platform-level role catalog and the permissions each role grants.",
          status: "active",
          requireAnyPermission: ["platform.roles.view", "platform.roles.manage"]
        },
        {
          to: "/admin/administration/identity/user-roles",
          label: "User role assignments",
          description: "Cross-workspace assignments; bulk role changes.",
          status: "active",
          requirePermissions: ["platform.roles.assign"]
        },
        {
          to: "/admin/access/effective-permissions",
          label: "Effective permissions",
          description: "What a given user can actually do, resolved across roles + scopes.",
          status: "active"
        },
        {
          to: "/admin/access/audit",
          label: "Access audit",
          description: "Suspicious access activity, login anomalies, permission changes.",
          status: "active",
          requireAnyPermission: ["platform.security.view", "platform.audit.view"]
        }
      ]
    },

    // --- 8. Monitoring & Operations -------------------------------------
    // Renamed from "Platform Operations" — same items, spec wording.
    {
      id: "monitoring-operations",
      label: "Monitoring & Operations",
      requireAnyPermission: [
        "platform.security.view",
        "platform.audit.view",
        "platform.health.view",
        "platform.feature_flags.view",
        "platform.support.view"
      ],
      items: [
        {
          to: "/admin/jobs",
          label: "Sync jobs",
          description: "Metadata, tracker, aggregate, replay, rollup, warehouse-load, publication.",
          status: "active"
        },
        {
          to: "/admin/operations/pipeline-health",
          label: "Pipeline health",
          description: "Cross-tenant data-pipeline summary: per-sync-type completion stats, watermarks, raw source-row counts.",
          status: "active",
          requireAnyPermission: ["platform.health.view", "platform.operations.manage"]
        },
        {
          to: "/admin/administration/operations/health",
          label: "Platform health",
          description: "Adapter health, connection states.",
          status: "active",
          requirePermissions: ["platform.health.view"]
        },
        {
          to: "/admin/operations/observability",
          label: "Observability",
          description: "Latency histograms, queue depth, error rate, pipeline metrics.",
          status: "active"
        },
        {
          to: "/admin/operations/metrics",
          label: "Metrics explorer",
          description: "Ad-hoc query over runtime metrics with prebuilt dashboards.",
          status: "active"
        },
        {
          to: "/admin/operations/notifications",
          label: "Notification centre",
          description: "Configure platform-wide alerts (paging, email, webhook).",
          status: "active"
        },
        {
          to: "/admin/operations/smtp",
          label: "SMTP settings",
          description: "Outbound email transport for the notification dispatcher.",
          status: "active",
          requireAnyPermission: ["platform.security.manage", "platform.security.view"]
        },
        {
          to: "/admin/operations/mail-templates",
          label: "Mail templates",
          description: "Preview and test the transactional templates shipped with @tallystream/mail.",
          status: "active",
          requireAnyPermission: ["platform.security.view", "platform.operations.manage"]
        },
        {
          to: "/admin/administration/operations/secrets",
          label: "Secrets inventory",
          description: "Encrypted secrets, rotation status, owners.",
          status: "active",
          requireAnyPermission: ["platform.secrets.status.view", "platform.security.view"]
        },
        {
          to: "/admin/administration/operations/audit",
          label: "Audit log",
          description: "Global audit events; filters by category, severity, actor.",
          status: "active",
          requirePermissions: ["platform.audit.view"]
        },
        {
          to: "/admin/administration/operations/feature-flags",
          label: "Feature flags",
          description: "Toggles for staged rollouts.",
          status: "active",
          requireAnyPermission: ["platform.feature_flags.view", "platform.feature_flags.manage"]
        },
        {
          to: "/admin/administration/operations/security-center",
          label: "Security centre",
          description: "Auth failures, rate-limit breaches, suspicious events.",
          status: "active",
          requireAnyPermission: ["platform.security.view", "platform.security.manage"]
        },
        {
          to: "/admin/administration/operations/support",
          label: "Support console",
          description: "Workspace support overview, tickets, impersonation entry.",
          status: "active",
          requireAnyPermission: ["platform.support.view", "platform.support.manage"]
        }
      ]
    },

    // --- 9. Settings ----------------------------------------------------
    {
      id: "settings",
      label: "Settings",
      requireAnyPermission: ["platform.tenants.manage", "platform.security.manage"],
      items: [
        {
          to: "/admin/administration/settings/general",
          label: "Platform settings",
          description: "Runtime configuration: default DHIS2 base URL, warehouse provider, strictness.",
          status: "active"
        },
        {
          to: "/admin/settings/pipeline",
          label: "Pipeline settings",
          description: "Rollup strictness, ingestion strictness, dead-letter policy.",
          status: "active"
        },
        {
          to: "/admin/settings/access-control",
          label: "Access control settings",
          description: "MFA enforcement, session policy, IP allowlists.",
          status: "active"
        },
        {
          to: "/admin/settings/cors",
          label: "CORS settings",
          description: "Allowed origins for embedded clients.",
          status: "active"
        },
        {
          to: "/admin/settings/environment",
          label: "Environment settings",
          description: "Environment badge, runtime banner, feature ramp.",
          status: "active"
        },
        {
          to: "/admin/settings/branding",
          label: "Branding settings",
          description: "Platform brand mark, accent colors, support contact.",
          status: "active"
        }
      ]
    }
  ]
};
